import type {
  SmartNotification,
  ContextualTrigger,
  NotificationAction,
  MasteryState,
  ProgressInsight,
  AIUserProfile,
  AIError,
} from '@coffeeplexity/shared';

// Import AINotificationType separately to handle potential import issues
import { AINotificationType } from '@coffeeplexity/shared';
import { logger } from '../utils/logger';

export interface BrewingReminderConfig {
  userId: string;
  skillLevel: number;
  preferredBrewMethods: string[];
  lastBrewTime?: string;
  reminderFrequency: 'daily' | 'weekly' | 'custom';
  customIntervalHours?: number;
  skillBasedTips: boolean;
}

export interface MilestoneAchievementData {
  userId: string;
  milestoneId: string;
  milestoneType:
    | 'skill_mastery'
    | 'streak'
    | 'journey_completion'
    | 'recipe_mastery';
  achievementLevel: number;
  personalizedMessage: string;
  celebrationStyle: 'minimal' | 'standard' | 'enthusiastic';
}

export interface ReEngagementConfig {
  userId: string;
  lastActiveDate: string;
  inactivityDays: number;
  engagementLevel: 'low' | 'medium' | 'high';
  preferredContent: string[];
  motivationalStyle: 'encouraging' | 'challenging' | 'educational';
}

export interface LearningReminderSchedule {
  userId: string;
  preferredTimes: string[];
  timezone: string;
  frequency: 'daily' | 'weekly' | 'custom';
  customDays?: number[];
  contextualFactors: {
    workSchedule?: string[];
    busyDays?: number[];
    preferredDuration?: number;
  };
}

export interface ABTestVariant {
  id: string;
  name: string;
  weight: number;
  config: {
    timing?: Partial<LearningReminderSchedule>;
    content?: {
      tone: 'casual' | 'professional' | 'enthusiastic';
      length: 'short' | 'medium' | 'long';
      includeEmoji: boolean;
    };
    frequency?: {
      multiplier: number;
      maxPerDay: number;
    };
  };
}

export interface NotificationDeliveryOptimization {
  userId: string;
  testId: string;
  variant: ABTestVariant;
  deliveryTime: string;
  actualDeliveryTime?: string;
  opened: boolean;
  clicked: boolean;
  dismissed: boolean;
  engagementScore: number;
  context: {
    deviceType: string;
    appState: string;
    batteryLevel?: number;
    networkType?: string;
  };
}

class ContextualNotificationTriggersService {
  private baseUrl: string;
  private apiKey: string;
  private abTestVariants: Map<string, ABTestVariant[]> = new Map();

  constructor(
    baseUrl: string = '/api/ai/notifications/contextual',
    apiKey: string = ''
  ) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
    this.initializeABTestVariants();
  }

  /**
   * Initialize A/B test variants for notification optimization
   */
  private initializeABTestVariants(): void {
    // Timing optimization variants
    const timingVariants: ABTestVariant[] = [
      {
        id: 'timing_morning',
        name: 'Morning Focus',
        weight: 0.25,
        config: {
          timing: {
            preferredTimes: ['08:00', '09:00', '10:00'],
          },
        },
      },
      {
        id: 'timing_evening',
        name: 'Evening Focus',
        weight: 0.25,
        config: {
          timing: {
            preferredTimes: ['18:00', '19:00', '20:00'],
          },
        },
      },
      {
        id: 'timing_adaptive',
        name: 'AI Adaptive',
        weight: 0.5,
        config: {
          timing: {
            preferredTimes: [], // Will be determined by AI
          },
        },
      },
    ];

    // Content optimization variants
    const contentVariants: ABTestVariant[] = [
      {
        id: 'content_casual',
        name: 'Casual Tone',
        weight: 0.33,
        config: {
          content: {
            tone: 'casual',
            length: 'short',
            includeEmoji: true,
          },
        },
      },
      {
        id: 'content_professional',
        name: 'Professional Tone',
        weight: 0.33,
        config: {
          content: {
            tone: 'professional',
            length: 'medium',
            includeEmoji: false,
          },
        },
      },
      {
        id: 'content_enthusiastic',
        name: 'Enthusiastic Tone',
        weight: 0.34,
        config: {
          content: {
            tone: 'enthusiastic',
            length: 'medium',
            includeEmoji: true,
          },
        },
      },
    ];

    // Frequency optimization variants
    const frequencyVariants: ABTestVariant[] = [
      {
        id: 'frequency_low',
        name: 'Low Frequency',
        weight: 0.33,
        config: {
          frequency: {
            multiplier: 0.7,
            maxPerDay: 2,
          },
        },
      },
      {
        id: 'frequency_standard',
        name: 'Standard Frequency',
        weight: 0.34,
        config: {
          frequency: {
            multiplier: 1.0,
            maxPerDay: 3,
          },
        },
      },
      {
        id: 'frequency_high',
        name: 'High Frequency',
        weight: 0.33,
        config: {
          frequency: {
            multiplier: 1.3,
            maxPerDay: 5,
          },
        },
      },
    ];

    this.abTestVariants.set('timing', timingVariants);
    this.abTestVariants.set('content', contentVariants);
    this.abTestVariants.set('frequency', frequencyVariants);
  }

  /**
   * Create brewing reminder system with skill-based tips
   */
  async createBrewingReminder(
    config: BrewingReminderConfig
  ): Promise<ContextualTrigger> {
    try {
      const skillBasedTips = await this.generateSkillBasedTips(
        config.skillLevel,
        config.preferredBrewMethods
      );

      const trigger: ContextualTrigger = {
        id: `brewing_reminder_${config.userId}_${Date.now()}`,
        name: 'Brewing Reminder with Skill Tips',
        condition: {
          type: 'time_based',
          frequency: config.reminderFrequency,
          customIntervalHours: config.customIntervalHours,
          lastBrewTime: config.lastBrewTime,
          skillLevel: config.skillLevel,
          preferredMethods: config.preferredBrewMethods,
        },
        action: {
          type: 'send_notification',
          template: 'brewing_reminder_with_tips',
          personalization: {
            skillBasedTips,
            skillLevel: config.skillLevel,
            preferredMethods: config.preferredBrewMethods,
            userName: await this.getUserName(config.userId),
          },
        },
        enabled: true,
        priority: 2,
        cooldownMinutes: config.reminderFrequency === 'daily' ? 1440 : 10080, // 24h or 7 days
      };

      const response = await fetch(`${this.baseUrl}/triggers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify(trigger),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create brewing reminder: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'BREWING_REMINDER_ERROR',
        message: error.message || 'Failed to create brewing reminder',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Build milestone achievement notifications with personalized messages
   */
  async createMilestoneAchievementNotification(
    data: MilestoneAchievementData
  ): Promise<SmartNotification> {
    try {
      const personalizedContent = await this.generateMilestoneContent(data);
      const optimalTiming = await this.calculateOptimalTiming(data.userId);

      const notification: SmartNotification = {
        id: `milestone_${data.milestoneId}_${Date.now()}`,
        userId: data.userId,
        type: AINotificationType.MILESTONE_ACHIEVEMENT,
        title: personalizedContent.title,
        content: personalizedContent.message,
        aiReasoning: personalizedContent.reasoning,
        optimalTiming: optimalTiming.toISOString(),
        confidence: 0.9,
        personalizationFactors: [
          `milestone_type:${data.milestoneType}`,
          `achievement_level:${data.achievementLevel}`,
          `celebration_style:${data.celebrationStyle}`,
        ],
        priority: 'high',
        channel: 'push',
        scheduledFor: optimalTiming.toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
        metadata: {
          milestoneId: data.milestoneId,
          milestoneType: data.milestoneType,
          achievementLevel: data.achievementLevel,
          celebrationStyle: data.celebrationStyle,
        },
      };

      const response = await fetch(`${this.baseUrl}/notifications/milestone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify(notification),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create milestone notification: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'MILESTONE_NOTIFICATION_ERROR',
        message: error.message || 'Failed to create milestone notification',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Implement re-engagement notifications with motivational content
   */
  async createReEngagementNotification(
    config: ReEngagementConfig
  ): Promise<SmartNotification> {
    try {
      const motivationalContent =
        await this.generateMotivationalContent(config);
      const optimalTiming = await this.calculateOptimalTiming(config.userId);

      const notification: SmartNotification = {
        id: `reengagement_${config.userId}_${Date.now()}`,
        userId: config.userId,
        type: AINotificationType.RE_ENGAGEMENT,
        title: motivationalContent.title,
        content: motivationalContent.message,
        aiReasoning: motivationalContent.reasoning,
        optimalTiming: optimalTiming.toISOString(),
        confidence: 0.8,
        personalizationFactors: [
          `inactivity_days:${config.inactivityDays}`,
          `engagement_level:${config.engagementLevel}`,
          `motivational_style:${config.motivationalStyle}`,
          `preferred_content:${config.preferredContent.join(',')}`,
        ],
        priority: 'medium',
        channel: 'push',
        scheduledFor: optimalTiming.toISOString(),
        expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(), // 48 hours
        metadata: {
          inactivityDays: config.inactivityDays,
          engagementLevel: config.engagementLevel,
          motivationalStyle: config.motivationalStyle,
          preferredContent: config.preferredContent,
        },
      };

      const response = await fetch(
        `${this.baseUrl}/notifications/reengagement`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
          },
          body: JSON.stringify(notification),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to create re-engagement notification: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'REENGAGEMENT_NOTIFICATION_ERROR',
        message: error.message || 'Failed to create re-engagement notification',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Add contextual learning reminders based on user schedule
   */
  async createContextualLearningReminder(
    schedule: LearningReminderSchedule
  ): Promise<ContextualTrigger> {
    try {
      const optimizedSchedule = await this.optimizeScheduleForUser(schedule);

      const trigger: ContextualTrigger = {
        id: `learning_reminder_${schedule.userId}_${Date.now()}`,
        name: 'Contextual Learning Reminder',
        condition: {
          type: 'schedule_based',
          preferredTimes: optimizedSchedule.preferredTimes,
          timezone: schedule.timezone,
          frequency: schedule.frequency,
          customDays: schedule.customDays,
          contextualFactors: schedule.contextualFactors,
          avoidBusyTimes: true,
        },
        action: {
          type: 'send_notification',
          template: 'contextual_learning_reminder',
          personalization: {
            preferredDuration:
              schedule.contextualFactors.preferredDuration || 15,
            userName: await this.getUserName(schedule.userId),
            timezone: schedule.timezone,
          },
        },
        enabled: true,
        priority: 1,
        cooldownMinutes: 60, // 1 hour cooldown
      };

      const response = await fetch(`${this.baseUrl}/triggers/learning`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify(trigger),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create learning reminder: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'LEARNING_REMINDER_ERROR',
        message: error.message || 'Failed to create learning reminder',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Create notification delivery optimization with A/B testing
   */
  async optimizeNotificationDelivery(
    userId: string,
    notificationType: AINotificationType
  ): Promise<{
    testId: string;
    variant: ABTestVariant;
    optimizedTiming: Date;
    deliveryConfig: any;
  }> {
    try {
      const testId = `ab_test_${userId}_${notificationType}_${Date.now()}`;

      // Select variants for this test
      const timingVariant = this.selectVariant('timing');
      const contentVariant = this.selectVariant('content');
      const frequencyVariant = this.selectVariant('frequency');

      // Combine variants into a single test configuration
      const combinedVariant: ABTestVariant = {
        id: `${timingVariant.id}_${contentVariant.id}_${frequencyVariant.id}`,
        name: `${timingVariant.name} + ${contentVariant.name} + ${frequencyVariant.name}`,
        weight:
          timingVariant.weight *
          contentVariant.weight *
          frequencyVariant.weight,
        config: {
          timing: timingVariant.config.timing,
          content: contentVariant.config.content,
          frequency: frequencyVariant.config.frequency,
        },
      };

      // Calculate optimized timing based on variant
      const optimizedTiming = await this.calculateOptimizedTiming(
        userId,
        combinedVariant.config.timing
      );

      // Create delivery configuration
      const deliveryConfig = {
        timing: optimizedTiming,
        content: combinedVariant.config.content,
        frequency: combinedVariant.config.frequency,
        testMetadata: {
          testId,
          variantId: combinedVariant.id,
          variantName: combinedVariant.name,
          startTime: new Date().toISOString(),
        },
      };

      // Record the A/B test assignment
      await this.recordABTestAssignment(userId, testId, combinedVariant);

      return {
        testId,
        variant: combinedVariant,
        optimizedTiming,
        deliveryConfig,
      };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'DELIVERY_OPTIMIZATION_ERROR',
        message: error.message || 'Failed to optimize notification delivery',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Record A/B test results for notification delivery
   */
  async recordABTestResult(
    optimization: NotificationDeliveryOptimization
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/ab-test/result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          ...optimization,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to record A/B test result: ${response.statusText}`
        );
      }
    } catch (error: any) {
      const aiError: AIError = {
        code: 'AB_TEST_RECORD_ERROR',
        message: error.message || 'Failed to record A/B test result',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Get A/B test performance analytics
   */
  async getABTestAnalytics(
    testId?: string,
    timeRange: 'day' | 'week' | 'month' = 'week'
  ): Promise<{
    tests: Array<{
      testId: string;
      variants: Array<{
        variantId: string;
        name: string;
        impressions: number;
        opens: number;
        clicks: number;
        engagementRate: number;
        confidence: number;
        isWinner: boolean;
      }>;
      status: 'running' | 'completed' | 'paused';
      startDate: string;
      endDate?: string;
    }>;
    summary: {
      totalTests: number;
      activeTests: number;
      averageEngagementImprovement: number;
      significantResults: number;
    };
  }> {
    try {
      const params = new URLSearchParams({
        timeRange,
        ...(testId && { testId }),
      });

      const response = await fetch(
        `${this.baseUrl}/ab-test/analytics?${params}`,
        {
          headers: {
            ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to get A/B test analytics: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'AB_TEST_ANALYTICS_ERROR',
        message: error.message || 'Failed to get A/B test analytics',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  // Private helper methods

  private async generateSkillBasedTips(
    skillLevel: number,
    preferredMethods: string[]
  ): Promise<string[]> {
    const tips: string[] = [];

    if (skillLevel < 0.3) {
      // Beginner tips
      tips.push('Start with a consistent grind size for better extraction');
      tips.push('Water temperature matters - aim for 195-205°F');
      if (preferredMethods.includes('pour_over')) {
        tips.push('Try the 30-second bloom for pour-over methods');
      }
    } else if (skillLevel < 0.7) {
      // Intermediate tips
      tips.push(
        'Experiment with different grind sizes to find your preference'
      );
      tips.push('Track your brewing ratios to replicate great cups');
      if (preferredMethods.includes('espresso')) {
        tips.push('Dial in your espresso with 25-30 second extraction times');
      }
    } else {
      // Advanced tips
      tips.push('Try adjusting your brewing technique based on bean origin');
      tips.push(
        'Consider water quality and mineral content for optimal extraction'
      );
      if (preferredMethods.includes('specialty')) {
        tips.push('Explore advanced techniques like pressure profiling');
      }
    }

    return tips;
  }

  private async generateMilestoneContent(
    data: MilestoneAchievementData
  ): Promise<{
    title: string;
    message: string;
    reasoning: string;
  }> {
    const userName = await this.getUserName(data.userId);

    let title: string;
    let message: string;

    switch (data.celebrationStyle) {
      case 'enthusiastic':
        title = `🎉 Amazing Achievement, ${userName}!`;
        message = `You've just ${this.getMilestoneDescription(data.milestoneType, data.achievementLevel)}! This is incredible progress - you're becoming a true coffee expert! ${data.personalizedMessage}`;
        break;
      case 'standard':
        title = `Great Job, ${userName}!`;
        message = `Congratulations on ${this.getMilestoneDescription(data.milestoneType, data.achievementLevel)}. ${data.personalizedMessage}`;
        break;
      case 'minimal':
        title = 'Milestone Reached';
        message = `${this.getMilestoneDescription(data.milestoneType, data.achievementLevel)}. ${data.personalizedMessage}`;
        break;
      default:
        title = `Well Done, ${userName}!`;
        message = `You've achieved ${this.getMilestoneDescription(data.milestoneType, data.achievementLevel)}. ${data.personalizedMessage}`;
    }

    return {
      title,
      message,
      reasoning: `Personalized milestone celebration based on user's preferred celebration style (${data.celebrationStyle}) and achievement type (${data.milestoneType})`,
    };
  }

  private async generateMotivationalContent(
    config: ReEngagementConfig
  ): Promise<{
    title: string;
    message: string;
    reasoning: string;
  }> {
    const userName = await this.getUserName(config.userId);

    let title: string;
    let message: string;

    switch (config.motivationalStyle) {
      case 'encouraging':
        title = `We Miss You, ${userName}!`;
        message = `It's been ${config.inactivityDays} days since your last coffee journey. Your progress is waiting for you - even 5 minutes can make a difference. Ready to brew something amazing?`;
        break;
      case 'challenging':
        title = `Ready for a Coffee Challenge?`;
        message = `${userName}, your coffee skills are calling! You've been away for ${config.inactivityDays} days. Time to level up and show what you've learned. Are you up for it?`;
        break;
      case 'educational':
        title = `New Coffee Insights Await`;
        message = `Hi ${userName}, we've curated some ${config.preferredContent.join(' and ')} content perfect for your skill level. Continue your coffee education journey today!`;
        break;
      default:
        title = `Welcome Back, ${userName}`;
        message = `Ready to continue your coffee journey? We have some great ${config.preferredContent.join(' and ')} content waiting for you.`;
    }

    return {
      title,
      message,
      reasoning: `Re-engagement message tailored to user's motivational style (${config.motivationalStyle}) and ${config.inactivityDays} days of inactivity`,
    };
  }

  private getMilestoneDescription(type: string, level: number): string {
    switch (type) {
      case 'skill_mastery':
        return `mastering a new coffee skill (Level ${level})`;
      case 'streak':
        return `maintaining a ${level}-day learning streak`;
      case 'journey_completion':
        return `completing your coffee journey`;
      case 'recipe_mastery':
        return `mastering ${level} new recipes`;
      default:
        return `reaching milestone level ${level}`;
    }
  }

  private async calculateOptimalTiming(userId: string): Promise<Date> {
    // This would typically call the AI service to get optimal timing
    // For now, return a reasonable default
    const now = new Date();
    const optimalHour = 9; // 9 AM as default
    const nextOptimal = new Date(now);

    if (now.getHours() >= optimalHour) {
      nextOptimal.setDate(nextOptimal.getDate() + 1);
    }

    nextOptimal.setHours(optimalHour, 0, 0, 0);
    return nextOptimal;
  }

  private async optimizeScheduleForUser(
    schedule: LearningReminderSchedule
  ): Promise<LearningReminderSchedule> {
    // Apply contextual optimizations
    const optimized = { ...schedule };

    // Avoid busy days if specified
    if (schedule.contextualFactors.busyDays) {
      optimized.customDays = optimized.customDays?.filter(
        day => !schedule.contextualFactors.busyDays!.includes(day)
      );
    }

    // Adjust timing based on work schedule
    if (schedule.contextualFactors.workSchedule) {
      // Filter out work hours from preferred times
      optimized.preferredTimes = schedule.preferredTimes.filter(time => {
        return !schedule.contextualFactors.workSchedule!.includes(time);
      });
    }

    return optimized;
  }

  private selectVariant(type: string): ABTestVariant {
    const variants = this.abTestVariants.get(type) || [];
    if (variants.length === 0) {
      throw new Error(`No variants found for type: ${type}`);
    }

    // Weighted random selection
    const random = Math.random();
    let cumulativeWeight = 0;

    for (const variant of variants) {
      cumulativeWeight += variant.weight;
      if (random <= cumulativeWeight) {
        return variant;
      }
    }

    // Fallback to first variant
    return variants[0];
  }

  private async calculateOptimizedTiming(
    userId: string,
    timingConfig?: Partial<LearningReminderSchedule>
  ): Promise<Date> {
    if (
      timingConfig?.preferredTimes &&
      timingConfig.preferredTimes.length > 0
    ) {
      // Use specified preferred times
      const now = new Date();
      const preferredTime = timingConfig.preferredTimes[0];
      const [hours, minutes] = preferredTime.split(':').map(Number);

      const nextTime = new Date(now);
      nextTime.setHours(hours, minutes, 0, 0);

      if (nextTime <= now) {
        nextTime.setDate(nextTime.getDate() + 1);
      }

      return nextTime;
    }

    // Fallback to AI-calculated optimal timing
    return this.calculateOptimalTiming(userId);
  }

  private async recordABTestAssignment(
    userId: string,
    testId: string,
    variant: ABTestVariant
  ): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/ab-test/assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          testId,
          variantId: variant.id,
          variantName: variant.name,
          assignedAt: new Date().toISOString(),
        }),
      });
    } catch (error) {
      logger.warn('Failed to record A/B test assignment:', error);
      // Don't throw error as this is not critical for functionality
    }
  }

  private async getUserName(userId: string): Promise<string> {
    try {
      // This would typically fetch from user service
      // For now, return a placeholder
      return 'Coffee Enthusiast';
    } catch (error) {
      return 'Coffee Enthusiast';
    }
  }
}

export const contextualNotificationTriggersService =
  new ContextualNotificationTriggersService();
